import type { PropType } from 'vue';
import type { Nullable } from '../../types';
import type { Option, TComboboxProps } from './TCombobox.types';
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { TDropdown, TDropdownItem } from '../TDropdown';
import { TTextInput } from '../TTextInput';
import { normalizeString } from './TCombobox.utils';

export const TCombobox = defineComponent({
  name: 'TCombobox',
  emits: ['input', 'select', 'update:modelValue'],
  props: {
    modelValue: String as PropType<TComboboxProps['modelValue']>,
    options: {
      type: Array as PropType<TComboboxProps['options']>,
      required: true,
    },
    placeholder: {
      type: String as PropType<TComboboxProps['placeholder']>,
      default: '',
    },
    label: String as PropType<TComboboxProps['label']>,
    helperText: String as PropType<TComboboxProps['helperText']>,
    disabled: Boolean as PropType<TComboboxProps['disabled']>,
  },
  setup(props, { emit, expose }) {
    const text = ref(props.modelValue ?? '');
    const selectedOption = ref<Nullable<Option>>(null);
    const isDropdownOpen = ref(false);
    const container = ref<Nullable<HTMLDivElement>>(null);
    const textInputRef = ref<Nullable<HTMLInputElement>>(null);

    const isDisabled = computed(() => props.disabled);

    const openDropdown = () => {
      isDropdownOpen.value = true;
    };
    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const unfocus = () => {
      textInputRef.value?.blur();
    };
    const resetTextValue = () => {
      if (selectedOption.value !== null) {
        text.value = selectedOption.value.label;
      } else {
        text.value = '';
      }
    };
    const resetSelectedOption = () => {
      selectedOption.value = null;
    };
    expose({ resetTextValue, resetSelectedOption });

    const handleFocus = () => {
      openDropdown();
    };
    const handleTextChange = (value: string) => {
      text.value = value;

      emit('input', value);
    };
    const handleClickOutside = (event: Event) => {
      if (!container.value) {
        return;
      }
      if (!isDropdownOpen.value) {
        return;
      }

      const isClickOutside = !container.value.contains(event.target as Node);
      if (isClickOutside) {
        closeDropdown();
        resetTextValue();
        unfocus();
      }
    };
    const handleSelection = (option: Option) => {
      text.value = option.label;
      selectedOption.value = option;

      emit('select', option.value);
      emit('update:modelValue', option.label);

      closeDropdown();
    };
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
        resetTextValue();
        unfocus();
      }
    };

    const filteredOptions = computed(() => {
      if (text.value === '') {
        return props.options;
      }

      return props.options.filter((option) => {
        const normalizedLabel = normalizeString(option.label);
        const normalizedFilterText = normalizeString(text.value);

        return normalizedLabel.includes(normalizedFilterText);
      });
    });

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      container.value?.addEventListener('keydown', handleKeydown);
    });
    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      container.value?.removeEventListener('keydown', handleKeydown);
    });

    const DropdownTrigger = () => (
      <TTextInput
        ref={textInputRef}
        modelValue={text.value}
        label={props.label}
        helperText={props.helperText}
        placeholder={props.placeholder}
        onFocus={handleFocus}
        onChange={handleTextChange}
        disabled={isDisabled.value}
      />
    );

    watch(
      () => props.modelValue,
      () => {
        text.value = props.modelValue ?? '';
      },
    );

    return () => (
      <div
        ref={container}
        role="combobox"
        aria-controls="menu"
        aria-expanded={isDropdownOpen.value}
      >
        <TDropdown open={isDropdownOpen.value} full-width>
          {{
            trigger: () => <DropdownTrigger />,
            default: () => filteredOptions.value.map((option) => (
              <TDropdownItem onClick={() => handleSelection(option)}>
                {option.label}
              </TDropdownItem>
            )),
          }}
        </TDropdown>
      </div>
    );
  },
});
